import { useEffect, useMemo } from 'react';

import {
  AggregatedChartConfigType,
  AggregatedChartYAxisOptions,
  ICardDataState,
} from '../../types.ts';
import { useDataFromConfig } from './aggregatedHighChart/useDataFromConfig.ts';
import { AggregatedHighChartPreData } from './AggregatedHighChartPreData.tsx';

interface AggregatedHighChartProps {
  config: AggregatedChartConfigType;
  setChartDataStat: (props: ICardDataState) => void;
  onChartNameChange?: (chartName: string | undefined) => void;
  yAxisOptions?: AggregatedChartYAxisOptions;
  lowDataCaptureThreshold?: number;
  normalizeTimeSeries?: string[];
}

/**
 * Self-contained aggregated chart: loads measurement ranges via `useDataFromConfig`, reports
 * loading / empty / rate-limit state through `setChartDataStat`, and renders via
 * `AggregatedHighChartPreData`.
 *
 * When a sibling UI already owns the same fetch (e.g. lice count rows), call `useDataFromConfig`
 * in the parent and render `AggregatedHighChartPreData` directly instead.
 */
export const AggregatedHighChart = ({
  config,
  setChartDataStat,
  onChartNameChange,
  yAxisOptions,
  lowDataCaptureThreshold,
  normalizeTimeSeries,
}: AggregatedHighChartProps) => {
  const { chartName, isLive, isTimeSeriesNormalized, data } = useDataFromConfig({
    config,
    normalizeTimeSeries,
  });

  const { allMeasurements, isLoading, isError, isFetched, isRateLimited } = data;

  const hasMeasurements = useMemo(
    () => Object.values(allMeasurements).flatMap(plot => Object.values(plot)).length !== 0,
    [allMeasurements]
  );

  useEffect(() => {
    setChartDataStat({
      isLoading: isLoading,
      isError: isError,
      isEmpty: isFetched && !hasMeasurements,
      isRateLimited: isRateLimited,
    });
  }, [hasMeasurements, isError, isFetched, isLoading, isRateLimited, setChartDataStat]);

  return (
    <AggregatedHighChartPreData
      allMeasurements={allMeasurements}
      hasMeasurements={hasMeasurements}
      isLive={isLive}
      isTimeSeriesNormalized={isTimeSeriesNormalized}
      config={config}
      chartName={chartName}
      onChartNameChange={onChartNameChange}
      yAxisOptions={yAxisOptions}
      lowDataCaptureThreshold={lowDataCaptureThreshold}
    />
  );
};
